import { useEffect, useState } from "react";
import { api, adminHeaders, getAdminToken, API, PROPERTY_NAMES } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Euro,
  TrendingUp,
  Sun,
  MoonStar,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const monthLabels = [
  "Gen", "Feb", "Mar", "Apr", "Mag", "Giu",
  "Lug", "Ago", "Set", "Ott", "Nov", "Dic",
];

const formatMonth = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-");
  return `${monthLabels[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const eur = (n) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);

export const RevenueTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/revenue", adminHeaders());
        setData(res.data);
      } catch (err) {
        toast.error("Errore caricamento incassi");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
      </div>
    );
  }
  if (!data) return null;

  const monthlyChart = data.by_month.map((m) => ({
    month: formatMonth(m.month),
    dayuse: m.dayuse,
    overnight: m.overnight,
    total: m.total,
  }));

  const roomChart = data.by_room.map((r) => ({
    name: r.room_name,
    property: r.property,
    total: r.total,
    count: r.count,
  }));

  const roomMax = Math.max(1, ...roomChart.map((r) => r.total));
  const hasData = data.total.count > 0;

  const now = new Date();
  const curMonth = now.toISOString().slice(0, 7);
  const cur = data.current_month;

  const downloadPDF = async (yearMonth) => {
    const [y, m] = yearMonth.split("-");
    try {
      const res = await fetch(
        `${API}/admin/report/monthly?year=${y}&month=${parseInt(m, 10)}`,
        { headers: { Authorization: `Bearer ${getAdminToken()}` } },
      );
      if (!res.ok) throw new Error("HTTP " + res.status);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${yearMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Report ${formatMonth(yearMonth)} scaricato`);
    } catch (err) {
      toast.error("Errore download PDF");
    }
  };

  const pctBadge =
    cur && cur.pct !== null && cur.previous_year > 0 ? (
      <span
        className={`inline-flex items-center gap-1 text-xs font-semibold pill px-2 py-0.5 ${
          cur.pct >= 0
            ? "bg-emerald-100 text-emerald-700"
            : "bg-red-100 text-red-700"
        }`}
        data-testid="yoy-badge"
      >
        {cur.pct >= 0 ? (
          <ArrowUpRight className="w-3 h-3" />
        ) : (
          <ArrowDownRight className="w-3 h-3" />
        )}
        {cur.pct >= 0 ? "+" : ""}
        {cur.pct}% vs {parseInt(curMonth.slice(0, 4), 10) - 1}
      </span>
    ) : null;

  return (
    <div data-testid="revenue-tab" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI
          label="Incasso totale"
          value={eur(data.total.total)}
          icon={<Euro className="w-4 h-4" />}
          testid="kpi-total-revenue"
        />
        <KPI
          label={`Mese corrente (${formatMonth(curMonth)})`}
          value={eur(cur?.current || 0)}
          icon={<TrendingUp className="w-4 h-4" />}
          badge={pctBadge}
          testid="kpi-current-month"
        />
        <KPI
          label="Da DayUse"
          value={eur(data.total.dayuse)}
          icon={<Sun className="w-4 h-4" />}
          accent="text-amber-600"
          testid="kpi-dayuse-revenue"
        />
        <KPI
          label="Da Pernottamenti"
          value={eur(data.total.overnight)}
          icon={<MoonStar className="w-4 h-4" />}
          accent="text-primary"
          testid="kpi-overnight-revenue"
        />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Button
          onClick={() => downloadPDF(curMonth)}
          className="btn-primary pill"
          size="sm"
          data-testid="pdf-current-month"
        >
          <FileDown className="w-4 h-4 mr-2" />
          Scarica PDF mese corrente
        </Button>
        <span className="text-xs text-muted-foreground">
          Report dettagliato con riepilogo incassi ed elenco prenotazioni
        </span>
      </div>

      {!hasData && (
        <div className="bg-white rounded-2xl border border-border p-14 text-center text-muted-foreground">
          Nessuna prenotazione ancora registrata. I grafici appariranno appena inizierai a incassare.
        </div>
      )}

      {hasData && (
        <>
          <div className="bg-white rounded-2xl border border-border p-6">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="font-display text-2xl text-primary">Incassi per mese</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest">
                  DayUse + Pernottamenti
                </div>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-4">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded bg-amber-400" />
                  DayUse
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded bg-primary" />
                  Pernottamenti
                </span>
              </div>
            </div>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={monthlyChart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64645e" }} />
                  <YAxis
                    tickFormatter={(v) => `€${v}`}
                    tick={{ fontSize: 12, fill: "#64645e" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(v) => eur(v)}
                    labelStyle={{ color: "#1c3e2e", fontWeight: 600 }}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e5e5e5",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                    }}
                  />
                  <Bar dataKey="dayuse" stackId="a" fill="#f59e0b" name="DayUse" />
                  <Bar dataKey="overnight" stackId="a" fill="#1c3e2e" name="Pernottamenti" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground w-full mb-1">
                Scarica PDF per mese
              </div>
              {data.by_month.map((m) => (
                <Button
                  key={m.month}
                  onClick={() => downloadPDF(m.month)}
                  variant="outline"
                  size="sm"
                  className="pill"
                  data-testid={`pdf-${m.month}`}
                >
                  <FileDown className="w-3 h-3 mr-1" />
                  {formatMonth(m.month)} · {eur(m.total)}
                </Button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border p-6">
            <div className="font-display text-2xl text-primary mb-1">
              Incassi per camera
            </div>
            <div className="text-xs text-muted-foreground uppercase tracking-widest mb-6">
              Classifica delle camere più redditizie
            </div>

            <div className="space-y-3">
              {roomChart.map((r) => {
                const pct = (r.total / roomMax) * 100;
                return (
                  <div key={r.name + r.property} data-testid={`room-revenue-${r.name}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <div>
                        <span className="font-medium text-primary">{r.name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">
                          {PROPERTY_NAMES[r.property] || r.property}
                        </span>
                      </div>
                      <div className="font-display text-lg text-secondary">
                        {eur(r.total)}
                        <span className="text-xs text-muted-foreground ml-2">
                          · {r.count} pren.
                        </span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {roomChart.length === 0 && (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  Nessun incasso registrato.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const KPI = ({ label, value, icon, accent = "text-primary", testid, badge }) => (
  <div className="bg-white rounded-2xl border border-border p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span className={accent}>{icon}</span>
    </div>
    <div className="font-display text-3xl text-primary">{value}</div>
    {badge && <div className="mt-2">{badge}</div>}
  </div>
);
